import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiChevronDown, HiArrowRightOnRectangle } from 'react-icons/hi2';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, firestore } from '../../../database/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../../context/AuthContext';
import LogoutConfirmation from '../../../components/ui/LogoutConfirmation';

const DevHeader = () => {
  const { logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async currentUser => {
      setLoading(true);
      if (currentUser) {
        setUser(currentUser);
        try {
          const userDocRef = doc(firestore, 'developer', currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          setUserProfile(userDocSnap.exists() ? userDocSnap.data() : null);
        } catch {
          setUserProfile(null);
        }
      } else {
        setUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => setShowLogoutConfirm(true);
  const confirmLogout = async () => {
    setShowLogoutConfirm(false);
    await logout();
  };

  if (!user && !loading) return null;

  const displayName =
    userProfile?.firstname && userProfile?.lastname
      ? `${userProfile.firstname} ${userProfile.lastname}`
      : user?.displayName || 'Developer';
  const firstName = userProfile?.firstname || user?.displayName?.split(' ')[0] || 'Developer';
  const initials =
    `${firstName.charAt(0)}${(userProfile?.lastname || user?.displayName?.split(' ')[1] || 'D').charAt(0)}`.toUpperCase();

  return (
    <motion.header
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="sticky top-0 z-40 flex w-full items-center justify-between border-b border-slate-700/50 bg-slate-900/95 px-4 py-3 backdrop-blur-xl md:px-6 md:py-4"
    >
      <div className="flex items-center gap-3">
        <h1 className="text-base font-semibold text-white md:text-lg">Developer Dashboard</h1>
      </div>

      <div className="relative" ref={dropdownRef}>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setDropdownOpen(open => !open)}
          className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800/50 px-3 py-2 transition-colors hover:border-slate-600 hover:bg-slate-800 md:gap-3"
        >
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-600 text-xs font-semibold text-white md:h-8 md:w-8">
            {initials}
          </div>
          <div className="hidden flex-col items-start text-left md:flex">
            <span className="text-sm font-medium text-white">{firstName}</span>
            <span className="text-xs text-slate-400">{user?.email}</span>
          </div>
          <motion.div animate={{ rotate: dropdownOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
            <HiChevronDown className="h-4 w-4 text-slate-400" />
          </motion.div>
        </motion.button>

        <AnimatePresence>
          {dropdownOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -8 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 mt-2 w-56 rounded-xl border border-slate-700 bg-slate-800 shadow-xl md:w-64"
            >
              <div className="border-b border-slate-700 px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 text-sm font-semibold text-white">
                    {initials}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-white">{displayName}</div>
                    <div className="truncate text-xs text-slate-400">{user?.email}</div>
                  </div>
                </div>
              </div>
              <div className="py-1">
                <motion.button
                  whileHover={{ backgroundColor: 'rgb(51 65 85)' }}
                  onClick={handleLogout}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-slate-300"
                >
                  <HiArrowRightOnRectangle className="h-4 w-4" />
                  Sign out
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <LogoutConfirmation
        isOpen={showLogoutConfirm}
        onConfirm={confirmLogout}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </motion.header>
  );
};

export default DevHeader;
